import { useNextEnvVars } from "@/components/context-providers/NextEnvVarsWrapper";
import { ButtonV2 } from "@/components/interactive/ButtonV2";
import { InputV2 } from "@/components/interactive/InputV2";
import { PageInitializationSpinner } from "@/components/loaders/spinner";
import {
  DEFAULT_GENERIC_ID_SET,
  GITHUB_PROVIDER,
  getCustomProviders,
  getDefaultProviders,
  Provider,
} from "@/components/login/login.utils";
import { IndividualSignupConsentNotice } from "@/components/login/IndividualSignupConsentNotice";
import { LoginFormError } from "@/components/login/LoginFormError";
import { LoginProviderButton } from "@/components/login/LoginProviderButton";
import { ErrorMessage } from "@/components/Message";
import { EMPTY_OBJECT } from "@/constants/stable-empty";
import { getSignOnProvidersForEmail } from "@/gen_src/proto/chalk/server/v1/sso-SsoService_connectquery";
import { isNonEmptyArray } from "@/utils/array-utils";
import { getHardcodedProvidersForEmail, LoginProvider } from "@/utils/auth-providers";
import { EMAIL_CHALLENGE_CODE_LENGTH, EMAIL_CHALLENGE_PROVIDER } from "@/utils/email-challenge";
import { isDefined } from "@/utils/function-utils";
import { useFormForSchema } from "@/utils/hooks/form/use-form-with-schema";
import { useGRPCMutation } from "@/utils/hooks/grpc/use-grpc-mutation";
import { amplitudeTrack } from "@/utils/hooks/use-amplitude-pageview";
import { NextAuthProvidersQuery } from "@/utils/hooks/use-auth-providers";
import { Routes } from "@/utils/routes";
import { cx } from "@emotion/css";
import { signIn, SignInOptions } from "next-auth/react";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import { useRouter } from "next/router";
import React, { useCallback, useMemo, useRef, useState } from "react";
import * as z from "zod";

const emailSchema = z.object({
  email: z.string(),
});

export interface LoginFormProps {
  isInternalKeycloakEnabled?: boolean;
  isSessionLogin?: boolean;
  message?: string;
  providersQuery: NextAuthProvidersQuery;
  providersOverride?: Provider[];
  purpose: string;
  signInOptions?: SignInOptions;
  showGithub?: boolean;
  showEmail?: boolean;
  /** Show the acquisition prompt below the sign-in controls. */
  showBookDemo?: boolean;
  trial?: boolean;
  helmCustomAuthProviders: string | null;
  customAuthProviders: string | null;
  /**
   * Runs — and must resolve — before the browser leaves for the identity provider, on
   * every sign-in path this form offers. See LoginProviderButton for why a rejection
   * aborts the sign-in rather than proceeding without it.
   */
  onBeforeSignIn?: () => Promise<void>;
  /** Receives provider pre-sign-in failures for flows that display their own error. */
  onProviderSignInError?: (error: unknown) => void;
  /** "ring": the sign-up design (v2 controls, "or" divider, placeholder-only email field). */
  appearance?: "default" | "ring";
}

interface EmailChallengeStageProps {
  email: string;
  code: string[];
  error: string | null;
  isSubmitting: boolean;
  onCodeChange: (index: number, value: string) => void;
  onCodeKeyDown: (index: number, event: React.KeyboardEvent) => void;
  onInputRef: (index: number, input: HTMLInputElement | null) => void;
  onResend: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onUseDifferentEmail: () => void;
}

const EmailChallengeStage: React.FC<EmailChallengeStageProps> = ({
  email,
  code,
  error,
  isSubmitting,
  onCodeChange,
  onCodeKeyDown,
  onInputRef,
  onResend,
  onSubmit,
  onUseDifferentEmail,
}) => (
  <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
    <div className="flex flex-col gap-5">
      <h2 className="mb-2 font-medium font-everett text-black-green-900 xl:text-xl">
        Check your email
      </h2>
      <p className="text-sm text-black-green-900">
        We sent a 6-digit code to <strong>{email}</strong>.
      </p>
    </div>
    <form className="mt-8 flex flex-col gap-5" onSubmit={onSubmit}>
      <div className="flex justify-center gap-2" role="group" aria-label="6-digit code">
        {code.map((digit, index) => (
          <input
            key={index}
            ref={(input) => onInputRef(index, input)}
            className={cx(
              "h-12 w-10 rounded-md border text-center text-lg text-black-green-900 shadow-sm focus:border-cds-bright-green-300 focus:outline-none focus:ring-cds-bright-green-300",
              error != null ? "border-red-500" : "border-zinc-300",
            )}
            type="text"
            inputMode="numeric"
            maxLength={EMAIL_CHALLENGE_CODE_LENGTH}
            autoComplete={index === 0 ? "one-time-code" : "off"}
            aria-label={`Digit ${index + 1}`}
            value={digit}
            onChange={(event) => onCodeChange(index, event.target.value)}
            onKeyDown={(event) => onCodeKeyDown(index, event)}
            onFocus={(event) => event.currentTarget.select()}
          />
        ))}
      </div>
      <ButtonV2 variant="primary" type="submit" className="w-full text-cds-sm" size="lg">
        Continue
      </ButtonV2>
    </form>
    {error != null ? (
      <p role="alert" className="mt-3 text-sm text-red-500">
        {error}
      </p>
    ) : null}
    <div className="mt-5 flex justify-center gap-2 text-sm">
      <button
        type="button"
        className="text-cds-bright-green-600 hover:underline"
        onClick={onResend}
        disabled={isSubmitting}
      >
        {isSubmitting ? "Sending…" : "Resend code"}
      </button>
      <span aria-hidden="true">·</span>
      <button
        type="button"
        className="text-cds-bright-green-600 hover:underline"
        onClick={onUseDifferentEmail}
      >
        Use a different email
      </button>
    </div>
  </div>
);

export const LoginForm: React.FC<LoginFormProps> = ({
  helmCustomAuthProviders,
  customAuthProviders,
  providersOverride,
  isSessionLogin,
  isInternalKeycloakEnabled,
  providersQuery,
  signInOptions,
  showGithub,
  showEmail,
  showBookDemo = true,
  purpose,
  trial,
  onBeforeSignIn,
  onProviderSignInError,
  appearance = "default",
}) => {
  const router = useRouter();
  const { NEXT_PUBLIC_AMPLITUDE_API_KEY } = useNextEnvVars();
  const { handleSubmit, register, setError, formState } = useFormForSchema(emailSchema);
  const customProviders = useMemo(
    () => getCustomProviders({ helmCustomAuthProviders, customAuthProviders }),
    [customAuthProviders, helmCustomAuthProviders],
  );
  const providers = providersQuery.providers ?? EMPTY_OBJECT;
  const defaultProviders = getDefaultProviders(showGithub === true);

  const [submittedSSOEmail, setSubmittedSSOEmail] = useState<string | null>(null);
  const [availbleProvidersForEmail, setAvailbleProvidersForEmail] = useState<LoginProvider[]>([]);
  const [emailChallengeEmail, setEmailChallengeEmail] = useState<string | null>(null);
  const [emailChallengeCode, setEmailChallengeCode] = useState<string[]>(() =>
    Array(EMAIL_CHALLENGE_CODE_LENGTH).fill(""),
  );
  const [emailChallengeError, setEmailChallengeError] = useState<string | null>(null);
  const [isEmailChallengeSubmitting, setIsEmailChallengeSubmitting] = useState(false);
  const emailChallengeInputs = useRef<Array<HTMLInputElement | null>>([]);

  const { mutateAsync: fetchSignOnProviders } = useGRPCMutation(getSignOnProvidersForEmail);

  const genericIds =
    customProviders != null
      ? new Set(customProviders.map((customProvider) => customProvider.id))
      : DEFAULT_GENERIC_ID_SET;

  const customSamlProviders = useMemo(
    () => customProviders?.filter((provider) => provider.type === "saml"),
    [customProviders],
  );

  let allProviders: Provider[] = isNonEmptyArray(providersOverride)
    ? providersOverride
    : Object.keys(providers ?? {})
        .map((k) => providers?.[k])
        .filter((p) => {
          return p.id !== EMAIL_CHALLENGE_PROVIDER && genericIds.has(p.id);
        });
  if (showGithub && !allProviders.some((provider) => provider.id === GITHUB_PROVIDER.id)) {
    allProviders = [...allProviders, GITHUB_PROVIDER];
  }
  let emailSignIn =
    showEmail === true &&
    providers[EMAIL_CHALLENGE_PROVIDER] != null &&
    (customProviders == null || genericIds.has(EMAIL_CHALLENGE_PROVIDER));

  // Configuration issue - check environment variables, but this shows default chalk.ai login
  if (allProviders.length === 0 && customSamlProviders?.length === 0) {
    allProviders = defaultProviders;
    emailSignIn = showEmail === true && providers[EMAIL_CHALLENGE_PROVIDER] != null;
  }

  const requestEmailChallenge = useCallback(
    async (email: string) => {
      const normalizedEmail = email.trim().normalize("NFKC").toLowerCase();
      if (!normalizedEmail || !normalizedEmail.includes("@")) {
        setError("email", { type: "custom", message: "Enter a valid email address." });
        return;
      }

      setEmailChallengeError(null);
      setIsEmailChallengeSubmitting(true);
      amplitudeTrack(NEXT_PUBLIC_AMPLITUDE_API_KEY, "click login option", {
        provider: EMAIL_CHALLENGE_PROVIDER,
        purpose,
      });
      try {
        if (onBeforeSignIn) {
          await onBeforeSignIn();
        }
        const response = await signIn(EMAIL_CHALLENGE_PROVIDER, {
          ...signInOptions,
          email: normalizedEmail,
          redirect: false,
        });
        if (response == null || response.error != null) {
          throw new Error(response?.error ?? "Email challenge request failed");
        }
        setEmailChallengeEmail(normalizedEmail);
        setEmailChallengeCode(Array(EMAIL_CHALLENGE_CODE_LENGTH).fill(""));
      } catch (err) {
        console.error("[Chalk] Failed to request email verification code", err);
        setEmailChallengeError("We could not send a code. Please try again.");
      } finally {
        setIsEmailChallengeSubmitting(false);
      }
    },
    [NEXT_PUBLIC_AMPLITUDE_API_KEY, onBeforeSignIn, purpose, setError, signInOptions],
  );

  const handleEmailChallengeCodeChange = useCallback((index: number, value: string) => {
    const digits = value.replace(/\D/g, "");
    setEmailChallengeError(null);
    setEmailChallengeCode((current) => {
      const next = [...current];
      digits
        .slice(0, EMAIL_CHALLENGE_CODE_LENGTH - index)
        .split("")
        .forEach((digit, offset) => {
          next[index + offset] = digit;
        });
      return next;
    });
    if (digits.length > 0) {
      emailChallengeInputs.current[
        Math.min(EMAIL_CHALLENGE_CODE_LENGTH - 1, index + digits.length)
      ]?.focus();
    }
  }, []);

  const handleEmailChallengeCodeKeyDown = useCallback(
    (index: number, event: React.KeyboardEvent) => {
      if (event.key === "Backspace" && emailChallengeCode[index] === "" && index > 0) {
        setEmailChallengeCode((current) => {
          const next = [...current];
          next[index - 1] = "";
          return next;
        });
        emailChallengeInputs.current[index - 1]?.focus();
        event.preventDefault();
      }
      if (event.key === "ArrowLeft" && index > 0) {
        emailChallengeInputs.current[index - 1]?.focus();
        event.preventDefault();
      }
      if (event.key === "ArrowRight" && index < EMAIL_CHALLENGE_CODE_LENGTH - 1) {
        emailChallengeInputs.current[index + 1]?.focus();
        event.preventDefault();
      }
    },
    [emailChallengeCode],
  );

  const handleEmailChallengeSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const token = emailChallengeCode.join("");
      if (token.length !== EMAIL_CHALLENGE_CODE_LENGTH || emailChallengeEmail == null) {
        setEmailChallengeError("Enter the 6-digit code from your email.");
        emailChallengeInputs.current
          .find((input, index) => input != null && emailChallengeCode[index] === "")
          ?.focus();
        return;
      }

      const callbackUrl = encodeURIComponent(
        signInOptions?.callbackUrl?.toString() ?? Routes.dashboard,
      );
      window.location.assign(
        `/api/auth/callback/${EMAIL_CHALLENGE_PROVIDER}?email=${encodeURIComponent(emailChallengeEmail)}&token=${token}&callbackUrl=${callbackUrl}`,
      );
    },
    [emailChallengeCode, emailChallengeEmail, signInOptions?.callbackUrl],
  );

  const resetEmailChallenge = useCallback(() => {
    setEmailChallengeEmail(null);
    setEmailChallengeCode(Array(EMAIL_CHALLENGE_CODE_LENGTH).fill(""));
    setEmailChallengeError(null);
    setSubmittedSSOEmail(null);
    setAvailbleProvidersForEmail([]);
  }, []);

  const onSignIn = useCallback(
    async (provider: LoginProvider) => {
      if (onBeforeSignIn) {
        try {
          await onBeforeSignIn();
        } catch (err) {
          console.error("[Chalk] Aborting sign-in: pre-sign-in step failed", err);
          return;
        }
      }
      if (provider.type === "provider") {
        const prov = providers[provider.id];
        amplitudeTrack(NEXT_PUBLIC_AMPLITUDE_API_KEY, "click login option", {
          provider: prov.id,
          purpose: purpose,
        });
        await signIn(prov.id, signInOptions);
      } else if (provider.type === "saml") {
        amplitudeTrack(NEXT_PUBLIC_AMPLITUDE_API_KEY, "click login option", {
          provider: "saml",
          purpose: purpose,
        });
        await router.push({
          pathname: "/api/auth/login/saml",
          query: {
            team: provider.team,
            callbackUrl: signInOptions?.callbackUrl,
            entityId: provider.entityId,
          },
        });
      }
    },
    [NEXT_PUBLIC_AMPLITUDE_API_KEY, onBeforeSignIn, providers, purpose, router, signInOptions],
  );

  const customDataProps = useMemo(() => ({ "data-cta-context": "login_form_cta" }), []);

  const onSubmitEmail = useCallback(
    async (email: string) => {
      const queryResponse = await fetchSignOnProviders({ email });

      let providers: LoginProvider[] = [];
      if (isNonEmptyArray(queryResponse.providers)) {
        try {
          providers = queryResponse.providers
            .map((signOnConfig): LoginProvider | undefined => {
              if (signOnConfig.providerConfig.case === "samlConfig") {
                return {
                  type: "saml",
                  team: signOnConfig.teamId,
                  entityId: signOnConfig.providerConfig.value.issuer,
                  name: signOnConfig.name,
                };
              }

              return undefined;
            })
            .filter(isDefined);
        } catch (err) {
          console.error("[Chalk] Failed to fetch providers, looking for hardcoded providers", err);
        }
      }

      if (!isNonEmptyArray(providers)) {
        const hardCodedProviders = getHardcodedProvidersForEmail(
          email,
          isSessionLogin,
          window.location.host,
          isInternalKeycloakEnabled,
        );
        if (hardCodedProviders.type === "error") {
          if (hardCodedProviders.error === "Your organization does not have SSO configured.") {
            await requestEmailChallenge(email);
          } else {
            setError("email", { type: "custom", message: hardCodedProviders.error });
          }
          return;
        } else {
          providers = hardCodedProviders.providers;
        }
      }

      if (providers.length === 1) {
        await onSignIn(providers[0]);
      } else {
        setSubmittedSSOEmail(email);
        setAvailbleProvidersForEmail(providers);
      }
    },
    [
      fetchSignOnProviders,
      isInternalKeycloakEnabled,
      isSessionLogin,
      onSignIn,
      requestEmailChallenge,
      setError,
    ],
  );

  const providerButtons = (
    <>
      {providersQuery.error ? (
        <>
          <ErrorMessage
            title={
              "Error loading login options, please contact support@chalk.ai if this error persists"
            }
            className={"px-2 py-4"}
          >
            <div className={"py-2"}>{providersQuery.error}</div>
          </ErrorMessage>
          {defaultProviders.map((provider) => (
            <LoginProviderButton
              key={provider.id}
              {...provider}
              signInOptions={signInOptions}
              purpose={purpose}
              appearance={appearance}
              onBeforeSignIn={onBeforeSignIn}
              onProviderSignInError={onProviderSignInError}
            />
          ))}
        </>
      ) : providersQuery.providers == null ? (
        <PageInitializationSpinner />
      ) : (
        [...allProviders, ...(customSamlProviders ?? [])].map((provider) => (
          <LoginProviderButton
            key={provider.id}
            {...provider}
            signInOptions={signInOptions}
            purpose={purpose}
            appearance={appearance}
            onBeforeSignIn={onBeforeSignIn}
            onProviderSignInError={onProviderSignInError}
          />
        ))
      )}
    </>
  );
  const ssoOptions = emailSignIn && availbleProvidersForEmail.length > 0 && (
    <div className={"flex flex-col gap-2"}>
      <h3 className="font-medium text-black-green-900 xl:text-xl">
        Available SSO options for{" "}
        <span className={cx(submittedSSOEmail && "text-marketing-chartreuse-dark")}>
          {submittedSSOEmail || "your email"}
        </span>
      </h3>
      <div className="flex flex-col gap-2">
        <ButtonV2
          variant="primary"
          type="button"
          className="w-full mt-3 text-cds-sm ease-in-out duration-200"
          size="lg"
          onClick={() => {
            setAvailbleProvidersForEmail([]);
            setSubmittedSSOEmail(null);
          }}
        >
          ← Use a different email
        </ButtonV2>
        {availbleProvidersForEmail.map((spec) => {
          if (spec.type === "saml") {
            return (
              <LoginProviderButton
                key={spec.team}
                id={spec.team}
                name={spec.name ?? "Team SSO Provider"}
                type={spec.type}
                entityId={spec.entityId}
                signInOptions={signInOptions}
                purpose={purpose}
                onBeforeSignIn={onBeforeSignIn}
                onProviderSignInError={onProviderSignInError}
              />
            );
          } else {
            const provider = providers[spec.id];
            return (
              <LoginProviderButton
                key={provider.id}
                id={provider.id}
                name={provider.name}
                type={provider.type}
                signInOptions={signInOptions}
                purpose={purpose}
                onBeforeSignIn={onBeforeSignIn}
                onProviderSignInError={onProviderSignInError}
              />
            );
          }
        })}
      </div>
    </div>
  );

  if (appearance === "ring" && emailChallengeEmail == null) {
    return (
      <div className="flex w-full flex-col">
        <form
          className="flex flex-col gap-6"
          method="POST"
          onSubmit={handleSubmit(async (data) => {
            await onSubmitEmail(data.email);
          })}
        >
          <div className="flex flex-col gap-3">{providerButtons}</div>
          {emailSignIn && availbleProvidersForEmail.length === 0 && (
            <>
              <div className="flex items-center gap-3 text-[13px] leading-4 text-[#33635C]">
                <span className="h-px flex-1 bg-[#E4E4E7]" />
                or
                <span className="h-px flex-1 bg-[#E4E4E7]" />
              </div>
              <div className="flex flex-col gap-3">
                <InputV2
                  {...register("email")}
                  data-testid="login-email"
                  id="email"
                  type="email"
                  label="Email"
                  hideLabel
                  placeholder="Enter email"
                  autoComplete="email"
                  required
                  disabled={formState.isSubmitting}
                  error={formState.errors.email?.message}
                />
                <ButtonV2
                  variant="primary"
                  data-testid="login-sso-submit"
                  type="submit"
                  className="w-full"
                  size="lg"
                  loading={formState.isSubmitting}
                >
                  Continue
                </ButtonV2>
                {emailChallengeError != null ? (
                  <p role="alert" className="text-sm text-cds-error">
                    {emailChallengeError}
                  </p>
                ) : null}
                {showBookDemo && (
                  <p className="text-[13px] leading-4 text-[#33635C]">
                    Not yet using Chalk?{" "}
                    <ChalkLink
                      unstyled
                      className="cursor-pointer font-medium text-[#177F65] hover:underline focus:outline-[#177F65]"
                      href={Routes.bookDemo}
                      {...customDataProps}
                    >
                      Book a demo ›
                    </ChalkLink>
                  </p>
                )}
              </div>
            </>
          )}
          {ssoOptions}
        </form>
        <div className="mt-4 space-y-4 text-[13px] leading-4 text-cds-ink-secondary">
          <p>
            Already have an account? <ChalkLink href={Routes.login}>Log in</ChalkLink>
          </p>
          <IndividualSignupConsentNotice />
        </div>
        <LoginFormError />
      </div>
    );
  }

  return (
    <>
      <div className="flex w-full max-w-96 flex-col justify-center">
        {emailChallengeEmail != null ? (
          <EmailChallengeStage
            email={emailChallengeEmail}
            code={emailChallengeCode}
            error={emailChallengeError}
            isSubmitting={isEmailChallengeSubmitting}
            onCodeChange={handleEmailChallengeCodeChange}
            onCodeKeyDown={handleEmailChallengeCodeKeyDown}
            onInputRef={(index, input) => {
              emailChallengeInputs.current[index] = input;
            }}
            onResend={() => void requestEmailChallenge(emailChallengeEmail)}
            onSubmit={handleEmailChallengeSubmit}
            onUseDifferentEmail={resetEmailChallenge}
          />
        ) : (
          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <>
              <div className="sm:mx-auto sm:w-full sm:max-w-md flex flex-col gap-5">
                <h2 className="mb-4 font-medium font-everett text-black-green-900 xl:text-xl">
                  Continue with
                </h2>
                {trial && (
                  <p className="mt-2 text-center text-zinc-600 xl:text-xl">
                    Or{" "}
                    <a
                      href="#"
                      className="font-medium text-cds-bright-green-400 hover:text-cds-bright-green-300 xl:text-xl"
                    >
                      try Chalk for free
                    </a>
                  </p>
                )}
              </div>
              <form
                className="flex flex-col gap-8"
                method="POST"
                onSubmit={handleSubmit(async (data) => {
                  await onSubmitEmail(data.email);
                })}
              >
                <div className="flex flex-col space-y-2 gap-1">{providerButtons}</div>
                {emailSignIn && availbleProvidersForEmail.length === 0 && (
                  <div>
                    <div className="flex flex-col gap-2">
                      <label
                        htmlFor="email"
                        className="block font-medium text-black-green-900 xl:text-xl"
                      >
                        Continue with your email
                      </label>
                      <div>
                        <input
                          {...register("email")}
                          data-testid="login-email"
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          disabled={formState.isSubmitting}
                          className="rounded-md appearance-none block w-full px-3 py-2 border border-zinc-300 shadow-sm placeholder-zinc-400 focus:outline-none focus:ring-cds-bright-green-300 focus:border-cds-bright-green-300 sm:text-cds-sm"
                        />
                        {formState.errors.email ? (
                          <span className="text-red-500 text-xs">
                            {formState.errors.email.message}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <ButtonV2
                      variant="primary"
                      data-testid="login-sso-submit"
                      type="submit"
                      className="w-full mt-3 text-cds-sm ease-in-out duration-200"
                      size="lg"
                      loading={formState.isSubmitting}
                    >
                      Continue with your email →
                    </ButtonV2>
                    {emailChallengeError != null ? (
                      <p role="alert" className="mt-3 text-sm text-cds-error">
                        {emailChallengeError}
                      </p>
                    ) : null}
                  </div>
                )}
                {ssoOptions}
                {(purpose === "signup" || purpose === "signup-link") && (
                  <IndividualSignupConsentNotice className="text-[13px] leading-4 text-cds-ink-secondary" />
                )}
                {showBookDemo && (
                  <div className={"font-[500] text-[14px] leading-[150%]"}>
                    Not yet using Chalk?{" "}
                    <ChalkLink
                      className={"link inline text-[#77A818]"}
                      href={Routes.bookDemo}
                      {...customDataProps}
                    >
                      Book a demo to take a test drive →
                    </ChalkLink>
                  </div>
                )}
              </form>
            </>
            <LoginFormError />
          </div>
        )}
      </div>
    </>
  );
};
